import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {IFiltersSliceState} from '../../filters/filtersSlice';
import {RootState} from '../../store';
import {getComparison} from './comparisonComponentAPI';

interface IComparisonComponentState {
  apiResponse: IComparisonComponentAPIResponse
  chartCategories: string[]
  chartDataPoints: IDataPoint[]
  chartHTMLSubtitle: string
  isLoading: boolean
  hasError: boolean
  firstLoadFulfilled: boolean
}

interface IDataPoint {
  y: number
  interviews: string
  avid: string
  hier: string
  parentAvid: string
  parentHier: string
  color: string
  isParent: number
  drillEnd: string
  drillStart: string
  name: string
}

export interface IComparisonComponentAPIResponse {
  avid: string
  values: IValue[]
  col: string
  stat: string
  q: string
  lastSo: any //fixme
  label: string
  small_text: string
  statistic: string
  segmentName: string
  segmentCategory: string
  surveyLabel: string
  periodLabel: string
  factor: number
}

interface IValue {
  avid: string
  color: string
  drillEnd: any //fixme
  drillStart: string
  interviews: string
  isParent: number
  label: string
  parentAvid: string
  parentLabel: string
  period: string
  values: string
}

const initialState: IComparisonComponentState = {
  apiResponse: {
    avid: '',
    values: [],
    col: '',
    stat: '',
    q: '',
    lastSo: '',
    label: '',
    small_text: '',
    statistic: '',
    segmentName: '',
    segmentCategory: '',
    surveyLabel: '',
    periodLabel: '',
    factor: 0,
  },
  chartCategories: [],
  chartDataPoints: [],
  chartHTMLSubtitle: '',
  isLoading: false,
  hasError: false,
  firstLoadFulfilled: false,
}

export const loadComparison = createAsyncThunk(
  'comparisonComponent/get',
  async (filters: IFiltersSliceState) => {
    const response = await getComparison(filters);
    return response;
  }
)

export const comparisonComponentSlice = createSlice({
  name: 'comparisonComponent',
  initialState,
  reducers: {
    setHTMLSubtitle: (state: IComparisonComponentState) => {
      const apiResponse = state.apiResponse;
      if (apiResponse) {
        let legend = `<small class="center-align">${apiResponse.small_text} | ${apiResponse.surveyLabel} | ${apiResponse.periodLabel}`;
        if (apiResponse.segmentName !== '') {
          legend += ` | ${apiResponse.segmentCategory}: ${apiResponse.segmentName}`;
        }
        legend += '</small>';
        state.chartHTMLSubtitle = legend;
      }
      return state;
    },
    setChartData: (state: IComparisonComponentState) => {
      state.chartCategories = [];
      const rawValues = state.apiResponse.values;
      state.chartDataPoints = rawValues.map((rawValue) => {
        state.chartCategories.push(rawValue.label);
        return {
          y: parseFloat(parseFloat(rawValue.values).toFixed(0)),
          interviews: rawValue.interviews,
          avid: rawValue.avid,
          hier: rawValue.avid,
          parentAvid: rawValue.parentAvid,
          parentHier: rawValue.parentAvid,
          color: rawValue.color,
          isParent: rawValue.isParent,
          drillEnd: rawValue.drillEnd,
          drillStart: rawValue.drillStart,
          name: state.apiResponse.statistic
        }
      });

      return state;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadComparison.pending, (state) => {
      state.isLoading = true;
      state.hasError = false;
    });
    builder.addCase(loadComparison.fulfilled, (state, action) => {
      if (action.payload) {
        state.apiResponse = action.payload;
        state.isLoading = false;
        state.hasError = false;
        if (!state.firstLoadFulfilled) {
          state.firstLoadFulfilled = true;
        }
      }
    });
    builder.addCase(loadComparison.rejected, (state, action) => {
      state.isLoading = false;
      if (action.error?.name !== 'AbortError') {
        state.hasError = true;
      }
    });
  },
});

export const selectComparisonComponentState = (state: RootState) => state.comparisonComponent;
export const selectComparisonAPIResponse = (state: RootState) => state.comparisonComponent.apiResponse;
export const selectIsComparisonDataLoading = (state: RootState) => state.comparisonComponent.isLoading;
export const selectComparisonHasError = (state: RootState) => state.comparisonComponent.hasError;
export const selectComparisonFirstLoadFulfilled = (state: RootState) => state.comparisonComponent.firstLoadFulfilled;
export const selectComparisonLabel = (state: RootState) => state.comparisonComponent.apiResponse.label
export const selectComparisonChartData = (state: RootState) => state.comparisonComponent.chartDataPoints
export const selectChartCategories = (state: RootState) => state.comparisonComponent.chartCategories;
export const {setHTMLSubtitle, setChartData} = comparisonComponentSlice.actions
export default comparisonComponentSlice.reducer;
